import React from "react";
import { useNavigate } from "react-router-dom";
import { IntentGroup } from "../../model_gen/intent";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";
import { cn } from "../../lib/utils";
import moment from "moment";

interface IntentGroupsTableProps {
  intentGroups: IntentGroup[];
  loading: boolean;
  sortConfig: SortConfig<IntentGroup>;
  onSort: (key: keyof IntentGroup) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
}

const IntentGroupsTable: React.FC<IntentGroupsTableProps> = ({
  intentGroups,
  loading,
  sortConfig,
  onSort,
  currentPage,
  onPageChange,
  pageSize,
  totalItems,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number | null) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number | null) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<IntentGroup>[] = [
    {
      key: "intent_name",
      header: "Intent Name",
      width: "w-[30%]",
      sortable: true,
      render: (group) => (
        <div className="font-gc-med text-foreground hover:text-maitai-green transition-colors">
          {group.intent_name}
        </div>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[25%]",
      sortable: true,
      render: (group) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(group.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(group.date_created)}
          </div>
        </div>
      ),
    },
    {
      key: "last_activity",
      header: "Last Activity",
      width: "w-[25%]",
      sortable: true,
      render: (group) => (
        <div className="space-y-1">
          <div className={cn(
            "text-sm font-gc-reg",
            group.last_activity && group.last_activity > 0 ? "text-foreground" : "text-muted-foreground"
          )}>
            {formatDate(group.last_activity)}
          </div>
          <div className={cn(
            "text-xs",
            group.last_activity && group.last_activity > 0 ? "text-muted-foreground" : "text-muted-foreground/60"
          )}>
            {formatTime(group.last_activity)}
          </div>
        </div>
      ),
    },
    {
      key: "request_count",
      header: "# Requests",
      width: "w-[20%]",
      sortable: true,
      render: (group) => (
        <div className={cn(
          "text-sm font-gc-reg",
          group.request_count && group.request_count > 0 ? "text-foreground" : "text-muted-foreground"
        )}>
          {group.request_count?.toLocaleString() || "0"}
        </div>
      ),
    },
  ];

  const handleRowClick = (group: IntentGroup) => {
    navigate(`/intent-group/${group.id}`);
  };

  return (
    <PaginatedTable
      data={intentGroups}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle="No intent groups found"
      emptyDescription="Intent groups will appear here when you create intents with the same action type."
      onRowClick={handleRowClick}
      rowClassName="hover:bg-accent/20 transition-colors"
      showPagination={true}
    />
  );
};

export default IntentGroupsTable; 